/**
 * useWork — carrega o TRABALHO a que um documento pertence.
 *
 * A tela passa a linha de onde o usuário está olhando (orçamento, contrato, OS
 * ou recibo) e recebe de volta a trilha inteira. Toda a lógica de "o que
 * pertence a este trabalho" mora em `lib/work.ts`; aqui só há I/O.
 *
 * Três decisões que não são óbvias:
 *
 * 1. **Offline-first de verdade.** `initialData` monta o trabalho a partir dos
 *    caches que o app já mantém (aquecidos pelo `OfflineDataCacheWarmup`) e
 *    `networkMode: 'offlineFirst'` deixa a query rodar sem rede em vez de ficar
 *    pausada. Junto vai `refetchOnMount: 'always'`: com `initialData` o TanStack
 *    trata o cache como FRESCO e, com `staleTime`, não revalidaria na montagem —
 *    a trilha mostraria o estado anterior à própria ação do usuário (#11/#80).
 *    Isso importa aqui mais que em outras telas, porque a tela do orçamento
 *    remonta logo depois de gerar um documento.
 * 2. **Gate de PAPEL antes de consultar o financeiro.** `finance_entries` é
 *    owner/admin-only na RLS: para um `member` a consulta voltaria VAZIA e a
 *    etapa de pagamento diria "não recebido", que é falso — o correto é "não sei
 *    dizer". Quando `canSeeFinance` é falso nem consultamos e devolvemos `null`,
 *    e o agregador liga `paymentEvidenceLimited` (#38).
 * 3. **Duas fases na busca, por causa do salto OS→recibo.** Um recibo gerado a
 *    partir de uma OS carrega só `service_order_id`; os ids das OSs do trabalho
 *    só se conhecem depois da primeira leva. A resposta inteira ainda passa por
 *    `collectWorkSources`, que é a autoridade única de seleção.
 *
 * NFS-e é a única origem sem cache offline (o módulo fiscal é online-only por
 * decisão de produto): offline a etapa fiscal simplesmente não aparece provada,
 * nunca com informação errada.
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { FINANCE_ENTRIES_CHANGED_EVENT } from '@/hooks/useFinance';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { supabase } from '@/integrations/supabase/client';
import {
  getCachedContracts,
  getCachedFinanceEntries,
  getCachedQuotes,
  getCachedReceipts,
  getCachedServiceOrders,
} from '@/lib/offlineDataCache';
import { resolveWorkGroup } from '@/hooks/useWorkLinks';
import {
  buildWork,
  collectWorkSources,
  mergeWorkSources,
  resolveWorkRoot,
  WORK_STAGE_ORDER,
  type Work,
  type WorkAnchor,
  type WorkRoot,
  type WorkRowPool,
  type WorkStageId,
} from '@/lib/work';

const WORK_KEY = 'work';

/** Ids temporários não existem no servidor — a corrente sai só do cache. */
const isOfflineId = (id?: string | null): boolean => Boolean(id) && String(id).startsWith('offline_');

function poolFromCache(userId: string, canSeeFinance: boolean): WorkRowPool {
  return {
    quotes: getCachedQuotes(userId) ?? [],
    contracts: getCachedContracts(userId) ?? [],
    serviceOrders: getCachedServiceOrders(userId) ?? [],
    receipts: getCachedReceipts(userId) ?? [],
    // Sem cache offline: o módulo fiscal é online-only.
    nfseDocuments: [],
    financeEntries: canSeeFinance ? (getCachedFinanceEntries(userId) ?? []) : null,
  };
}

/**
 * A OS de um recibo, para descobrir o `quote_id` dela (o 2º salto da raiz).
 * Tenta o cache antes da rede: offline é o único caminho, e online evita uma
 * ida a mais quando o warmup já trouxe a OS.
 */
async function findServiceOrderQuoteId(
  userId: string,
  serviceOrderId: string,
): Promise<string | null> {
  const cached = (getCachedServiceOrders(userId) ?? []).find(
    (o: any) => String(o?.id) === serviceOrderId,
  );
  if (cached) return cached.quote_id ?? null;
  if (isOfflineId(serviceOrderId)) return null;
  try {
    const { data } = await supabase
      .from('service_orders')
      .select('id, quote_id')
      .eq('id', serviceOrderId)
      .maybeSingle();
    return (data as any)?.quote_id ?? null;
  } catch {
    return null;
  }
}

export interface UseWorkResult {
  work: Work | null;
  /** As raízes do trabalho. Mais de uma = agrupado explicitamente (Fase 3). */
  roots: WorkRoot[];
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Revalida quando o financeiro muda em OUTRA tela da mesma sessão.
 *
 * A etapa de pagamento é provada por `finance_entries`, mas quem grava é o
 * `QuoteFinancePrompt` ou a `/financeiro` — queries diferentes, que invalidam a
 * própria chave e não esta. Sem isto, o usuário registra o recebimento e a
 * trilha continua mostrando a etapa vazia até remontar (#80). Medido em device.
 *
 * O `refetch` entra por ref para o efeito não reassinar a cada render (ele muda
 * de identidade sempre) — reassinar por si só é inofensivo, mas o listener
 * órfão do render anterior não é.
 */
export function useRefetchOnFinanceChange(refetch: () => void, enabled = true): void {
  const latest = useRef(refetch);
  latest.current = refetch;
  useEffect(() => {
    if (!enabled) return;
    const handler = () => latest.current();
    window.addEventListener(FINANCE_ENTRIES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(FINANCE_ENTRIES_CHANGED_EVENT, handler);
  }, [enabled]);
}

export function useWork(anchor: WorkAnchor | null): UseWorkResult {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { canSeeFinance } = useRole();
  const contractsFeature = useFeature('contracts');
  const nfseFeature = useFeature('nfse_emission');

  const userId = user?.id ?? null;
  const companyId = activeCompany?.id ?? null;

  /**
   * A trilha mostra as cinco etapas para todo mundo — é ela que torna contrato e
   * nota visíveis para quem não assina. O plano restringe só o CONVITE: sugerir
   * "gerar contrato" a quem vai bater num paywall transforma o próximo passo num
   * anúncio. Quem não pode não é convidado, mas continua vendo que a etapa
   * existe.
   */
  const suggestable: WorkStageId[] = WORK_STAGE_ORDER.filter((id) => {
    if (id === 'contract') return contractsFeature.allowed;
    if (id === 'nfse') return nfseFeature.allowed;
    return true;
  });

  const anchorKey = anchor ? `${anchor.kind}:${anchor.id}` : null;

  const query = useQuery({
    // `canSeeFinance` entra na chave: se o papel mudar (troca de empresa), o
    // trabalho não pode reaproveitar um cache montado sob outra permissão.
    queryKey: [WORK_KEY, companyId, anchorKey, anchor?.quoteId ?? null, canSeeFinance],
    enabled: Boolean(userId && anchor?.id),
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    // `initialData` sem carimbo de tempo é tratado como FRESCO, e
    // `refetchOnMount` só é avaliado na MONTAGEM — mas esta query nasce
    // DESABILITADA (`userId` vem do AuthContext, que restaura a sessão de forma
    // assíncrona). Quando ela é habilitada, a montagem já passou e o dado
    // semeado do cache parece bom: nenhuma busca acontece. Resultado medido em
    // device: tudo que existe SÓ no servidor — o agrupamento explícito, um
    // documento criado em outro aparelho — ficava invisível até algo forçar um
    // refetch. Marcar o dado semeado como velho (época 0) é o que garante a
    // primeira busca.
    initialDataUpdatedAt: 0,
    initialData: (): WorkRowPoolResult | undefined => {
      if (!userId || !anchor?.id) return undefined;
      const pool = poolFromCache(userId, canSeeFinance);
      const cachedOrder = anchor.serviceOrderId
        ? (pool.serviceOrders ?? []).find((o: any) => String(o?.id) === String(anchor.serviceOrderId))
        : null;
      const root = resolveWorkRoot(anchor, cachedOrder?.quote_id ?? null);
      return { roots: [root], pools: [pool] };
    },
    queryFn: async (): Promise<WorkRowPoolResult> => {
      if (!userId || !anchor?.id) {
        return { roots: [], pools: [] };
      }

      const serviceOrderQuoteId =
        anchor.kind === 'receipt' && anchor.serviceOrderId && !anchor.quoteId
          ? await findServiceOrderQuoteId(userId, String(anchor.serviceOrderId))
          : null;
      const root = resolveWorkRoot(anchor, serviceOrderQuoteId);

      // Raiz temporária: nada disso existe no servidor ainda.
      if (isOfflineId(root.id) || !companyId) {
        return { roots: [root], pools: [poolFromCache(userId, canSeeFinance)] };
      }

      /**
       * Agrupamento explícito (Fase 3). Lido AQUI, e não por outra query, para
       * não coordenar dois `useQuery`: o pool tem que ser buscado por raiz, e as
       * raízes só se conhecem depois dos vínculos. Sem vínculo, o grupo é a
       * própria raiz e nada muda em relação ao comportamento derivado.
       */
      let roots: WorkRoot[] = [root];
      try {
        const { data: links } = await (supabase as any)
          .from('work_links')
          .select('id, work_key, document_kind, document_id')
          .eq('company_id', companyId);
        roots = resolveWorkGroup(root, (links ?? []) as any);
      } catch {
        // Sem rede o agrupamento simplesmente não se aplica — a trilha derivada
        // continua correta, só não unida. Melhor que a tela inteira falhar.
      }

      /**
       * Sem rede o cliente do Supabase REJEITA (não devolve `{ error }`), e uma
       * rejeição dentro de `Promise.all` descarta as outras respostas — o
       * fallback por origem nunca rodaria e a trilha sumiria inteira por causa
       * de uma tabela. Ver armadilha #58.
       */
      const safe = <T,>(p: PromiseLike<T>): Promise<T | { data: null; error: true }> =>
        Promise.resolve(p).then(
          (r) => r,
          () => ({ data: null, error: true as const }),
        );

      const scoped = (table: string, columns: string) =>
        supabase.from(table as any).select(columns).eq('company_id', companyId);

      /** As linhas candidatas de UMA raiz. A seleção fina fica no módulo puro. */
      const loadPool = async (target: WorkRoot): Promise<WorkRowPool> => {
        const byQuote = target.kind === 'quote';
        const [quotes, contracts, serviceOrders, nfseDocuments, financeEntries] = await Promise.all([
          byQuote ? safe(scoped('quotes', QUOTE_COLUMNS).eq('id', target.id)) : EMPTY,
          byQuote
            ? safe(scoped('contracts', CONTRACT_COLUMNS).eq('quote_id', target.id))
            : target.kind === 'contract'
              ? safe(scoped('contracts', CONTRACT_COLUMNS).eq('id', target.id))
              : EMPTY,
          byQuote
            ? safe(scoped('service_orders', ORDER_COLUMNS).eq('quote_id', target.id))
            : target.kind === 'service_order'
              ? safe(scoped('service_orders', ORDER_COLUMNS).eq('id', target.id))
              : EMPTY,
          byQuote
            ? safe(
                scoped('nfse_documents', NFSE_COLUMNS)
                  .eq('quote_id', target.id)
                  .is('discarded_at', null),
              )
            : EMPTY,
          byQuote && canSeeFinance
            ? safe(scoped('finance_entries_with_status', FINANCE_COLUMNS).eq('quote_id', target.id))
            : EMPTY,
        ]);

        const cached = poolFromCache(userId, canSeeFinance);
        const pick = (result: any, fallback: any[]): any[] =>
          result?.error ? fallback : ((result?.data ?? []) as any[]);

        const orders = pick(serviceOrders, cached.serviceOrders ?? []);

        // 2ª fase: os recibos do trabalho penduram no orçamento OU em qualquer
        // uma das OSs — e os ids delas só se conhecem agora.
        const orderIds = orders
          .map((o: any) => String(o?.id))
          .filter((id) => id && !id.startsWith('offline_'));
        const receiptFilters = [
          byQuote ? `quote_id.eq.${target.id}` : null,
          target.kind === 'receipt' ? `id.eq.${target.id}` : null,
          target.kind === 'service_order' ? `service_order_id.eq.${target.id}` : null,
          orderIds.length > 0 ? `service_order_id.in.(${orderIds.join(',')})` : null,
        ].filter(Boolean) as string[];
        const receipts =
          receiptFilters.length > 0
            ? await safe(scoped('receipts', RECEIPT_COLUMNS).or(receiptFilters.join(',')))
            : { data: [], error: null };

        return {
          quotes: pick(quotes, cached.quotes ?? []),
          contracts: pick(contracts, cached.contracts ?? []),
          serviceOrders: orders,
          receipts: pick(receipts, cached.receipts ?? []),
          nfseDocuments: pick(nfseDocuments, []),
          financeEntries: canSeeFinance ? pick(financeEntries, cached.financeEntries ?? []) : null,
        };
      };

      // Uma busca por raiz. São 1 na esmagadora maioria dos casos e 2–3 num
      // trabalho agrupado — buscar tudo de uma vez exigiria filtros `in` sobre
      // ids que não cabem numa URL e perderia o recorte por raiz.
      const pools = await Promise.all(roots.map(loadPool));
      return { roots, pools };
    },
  });

  const result = query.data;
  const work = result?.roots?.[0]?.id
    ? buildWork(
        // Cada raiz traz a PRÓPRIA corrente pela mesma função do caminho não
        // agrupado; a união só junta o que elas devolveram.
        mergeWorkSources(result.roots.map((r, i) => collectWorkSources(r, result.pools[i] ?? {}))),
        { suggestable },
      )
    : null;

  useRefetchOnFinanceChange(() => void query.refetch(), Boolean(anchor?.id));

  return {
    work,
    roots: result?.roots ?? [],
    isLoading: query.isLoading,
    refetch: () => void query.refetch(),
  };
}

interface WorkRowPoolResult {
  /** A raiz do usuário primeiro; as demais são as agrupadas explicitamente. */
  roots: WorkRoot[];
  /** Um pool por raiz, na mesma ordem. */
  pools: WorkRowPool[];
}

const EMPTY = Promise.resolve({ data: [] as any[], error: null });

/**
 * Colunas de cada origem. Exportadas porque a LISTA (`useWorks`) lê as mesmas:
 * se ela selecionasse menos, o mesmo trabalho apareceria numa fase na lista e
 * noutra ao abrir o documento — divergência sem erro nenhum na tela.
 */
export const QUOTE_COLUMNS = 'id, title, total, status, created_at, document_type, client_name';
export const CONTRACT_COLUMNS =
  'id, title, total, status, created_at, sequence_id, quote_id, client_name';
export const ORDER_COLUMNS =
  'id, title, total, status, created_at, sequence_id, quote_id, client_name';
export const RECEIPT_COLUMNS =
  'id, description, amount, issue_date, created_at, cancelled_at, sequence_id, quote_id, service_order_id, payer_name';
export const NFSE_COLUMNS = 'id, numero, status, valor_servico, emitida_em, created_at, quote_id';
export const FINANCE_COLUMNS =
  'id, description, amount, kind, status, computed_status, due_date, paid_date, quote_id';
